import { useCallback, useEffect, useState } from 'react'
import logger from '../utils/logger'

// Generate a session ID for funnel tracking
const generateSessionId = () => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 15)
  return `wa_${timestamp}_${random}`
}

// Session storage key for WhatsApp tracking
const WHATSAPP_SESSION_KEY = 'whatsapp_tracking_session'
const SESSION_EXPIRY_KEY = 'whatsapp_tracking_session_expiry'
const SESSION_DURATION = 30 * 60 * 1000 // 30 minutes

// Initialize session ID from storage or generate new one
const initializeSessionId = () => {
  try {
    const stored = sessionStorage.getItem(WHATSAPP_SESSION_KEY)
    const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY)

    // Check if we have a valid stored session
    if (stored && expiry) {
      const expiryTime = parseInt(expiry, 10)
      if (Date.now() < expiryTime) {
        logger.debug('Using existing WhatsApp tracking session:', stored)
        return stored
      }
      logger.debug('WhatsApp tracking session expired, generating new one')
      // Clean up expired session
      sessionStorage.removeItem(WHATSAPP_SESSION_KEY)
      sessionStorage.removeItem(SESSION_EXPIRY_KEY)
    }

    // Generate new session ID
    const newSessionId = generateSessionId()
    logger.debug('Generated new WhatsApp tracking session:', newSessionId)
    return newSessionId
  } catch (error) {
    // Fallback if sessionStorage is not available
    logger.warn('SessionStorage not available for tracking session, using temporary session:', error)
    return generateSessionId()
  }
}

/**
 * Hook for managing stable WhatsApp tracking session IDs
 * Uses sessionStorage to persist session across page reloads
 * Automatically expires sessions after 30 minutes of inactivity
 */
export default function useTrackingSession() {
  // State-managed session ID that updates properly when startNewSession is called
  const [sessionId, setSessionId] = useState(() => initializeSessionId())

  // Persist session ID and update expiry on mount and when sessionId changes
  useEffect(() => {
    try {
      const expiryTime = Date.now() + SESSION_DURATION
      sessionStorage.setItem(WHATSAPP_SESSION_KEY, sessionId)
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString())
    } catch (error) {
      logger.warn('Failed to persist WhatsApp tracking session:', error)
    }
  }, [sessionId])

  // Function to start a new session (useful for setup flow restarts)
  const startNewSession = useCallback(() => {
    try {
      const newSessionId = generateSessionId()
      const expiryTime = Date.now() + SESSION_DURATION

      // Update React state first
      setSessionId(newSessionId)

      // Update storage (this will also be handled by useEffect, but we do it here for immediate consistency)
      sessionStorage.setItem(WHATSAPP_SESSION_KEY, newSessionId)
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString())

      logger.debug('Started new WhatsApp tracking session:', newSessionId)
      return newSessionId
    } catch (error) {
      logger.warn('Failed to start new WhatsApp tracking session:', error)
      const fallbackSessionId = generateSessionId()
      setSessionId(fallbackSessionId)
      return fallbackSessionId
    }
  }, [])

  // Function to extend current session expiry (called on user activity)
  const extendSession = useCallback(() => {
    try {
      const expiryTime = Date.now() + SESSION_DURATION
      sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString())
      // Also re-persist session ID to handle edge cases where storage might be partially cleared
      sessionStorage.setItem(WHATSAPP_SESSION_KEY, sessionId)
      logger.debug('Extended WhatsApp tracking session expiry')
    } catch (error) {
      logger.warn('Failed to extend WhatsApp tracking session:', error)
    }
  }, [sessionId])

  // Function to clear session (useful for logout or privacy)
  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem(WHATSAPP_SESSION_KEY)
      sessionStorage.removeItem(SESSION_EXPIRY_KEY)
      logger.debug('Cleared WhatsApp tracking session')
      // Generate new session ID after clearing
      const newSessionId = generateSessionId()
      setSessionId(newSessionId)
    } catch (error) {
      logger.warn('Failed to clear WhatsApp tracking session:', error)
    }
  }, [])

  return {
    sessionId,
    startNewSession,
    extendSession,
    clearSession
  }
}
